/**
 * Tool Call Display
 *
 * Displays tool execution status and results in the assistant chat.
 * Updated to include all tools from the unified tool registry.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Wrench, Check, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { ToolCall } from '@/contexts/GlobalAssistantContext';

interface ToolCallDisplayProps {
  toolCall: ToolCall;
}

// Tool descriptions for all unified tools
const TOOL_DESCRIPTIONS: Record<string, string> = {
  // Editor tools
  validate_config: 'Validating agent configuration',
  list_mcp_servers: 'Listing available MCP servers',
  list_tools: 'Listing available tools',
  list_skills: 'Listing available skills',
  // Eval tools
  list_evals: 'Listing evaluations',
  get_eval: 'Getting evaluation details',
  list_runs: 'Listing evaluation runs',
  start_run: 'Starting evaluation run',
  get_run_status: 'Checking run status',
  get_run_results: 'Getting run results',
  compare_variants: 'Comparing variants',
  cancel_run: 'Cancelling run',
  diagnose_failures: 'Diagnosing failures',
};

export function ToolCallDisplay({ toolCall }: ToolCallDisplayProps) {
  const [isOpen, setIsOpen] = useState(false);

  const statusIcon = {
    pending: <Wrench className="h-3 w-3 text-muted-foreground" />,
    running: <Loader2 className="h-3 w-3 text-blue-500 animate-spin" />,
    complete: <Check className="h-3 w-3 text-green-500" />,
    error: <AlertCircle className="h-3 w-3 text-destructive" />,
  }[toolCall.status];

  const description = TOOL_DESCRIPTIONS[toolCall.name] || `Running ${toolCall.name}`;

  const formatResult = (result: unknown): string => {
    if (typeof result === 'string') return result;
    try {
      return JSON.stringify(result, null, 2);
    } catch {
      return String(result);
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'w-full justify-start h-auto py-1.5 px-2 text-xs font-normal',
            'bg-muted/50 hover:bg-muted border border-border rounded-md'
          )}
        >
          <span className="flex items-center gap-2 flex-1">
            {statusIcon}
            <span className="text-muted-foreground">{description}</span>
            {toolCall.status === 'complete' && toolCall.result && (
              <span className="ml-auto flex items-center text-muted-foreground">
                {isOpen ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
              </span>
            )}
          </span>
        </Button>
      </CollapsibleTrigger>
      {toolCall.status === 'complete' && toolCall.result && (
        <CollapsibleContent>
          <div className="mt-1 p-2 bg-muted/30 rounded-md border text-xs">
            <pre className="whitespace-pre-wrap break-words max-h-[200px] overflow-y-auto">
              {formatResult(toolCall.result)}
            </pre>
          </div>
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}

export default ToolCallDisplay;
